/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  ActionDispatcher,
  ActionFlow,
  createFlow,
  exitFlow,
  goToStep,
  LogicalAction,
} from '@gh/common-shared';
import { Message } from '@gh/core-messages';
import { translationTuple, TranslationTuple } from '@gh/core-mls';
import { NotificationService, yesNoConfirmation } from '@gh/core-ui';
import { fromPairs, map } from 'lodash';
import { CommonActions, CommonFlowState } from '../common-actions.service';

export interface ListToggleItemPipelineConfig<S> {
  question: string | TranslationTuple;
  execute: LogicalAction<S>;
  validate?: LogicalAction<S>;
  finish?: ProcedureFn<S>;
  successMessage?: string | TranslationTuple;
}

export interface CheckUsageToggleItemPipelineConfig<S> {
  messages: {
    confirmUsed?: string | TranslationTuple | Message;
    confirmNotUsed?: string | TranslationTuple | Message;
    notificationUsed?: Message;
  };
  executes: { [name: string]: LogicalAction<S> };
  validate?: LogicalAction<S>;
  finish?: ProcedureFn<S>;
  successMessage?: string | TranslationTuple;
}

@Injectable()
export class ListActionPipelines {
  constructor(private commonActions: CommonActions, private notificationService: NotificationService) {

  }

  toggleItem<S extends CommonFlowState<any>>(pipeline: ListToggleItemPipelineConfig<S>): ActionFlow<S> {
    const question = translationTuple(pipeline.question);
// @ts-expect-error Legacy typing suppression
    return createFlow({
      'start': pipeline.validate ? pipeline.validate.bindAs('validate.success', 'notifications') : goToStep(
        'validate.success'),
      'validate.success': this.commonActions.confirmation(yesNoConfirmation(question.id, question.params), {
        yes: 'confirmation.yes',
        no: 'finish',
      }),
      'confirmation.yes': pipeline.execute.bindAs('execute.success', 'notifications'),
      'execute.success': (dispatch: ActionDispatcher, state: S) => {
        if (pipeline.successMessage) {
          const { id, params } = translationTuple(pipeline.successMessage);
          this.notificationService.success(id, params);
        }

        if (pipeline.finish) {
          pipeline.finish(state);
        }

        dispatch('finish', state);
      },
      'notifications': this.commonActions.notifications().bindAs('finish'),
      '*': exitFlow(),
    });
  }

  checkUsageToggleItem<S extends CommonFlowState<any>>(pipeline: CheckUsageToggleItemPipelineConfig<S>): ActionFlow<S> {
    const messages = fromPairs(map(pipeline.messages, (message: string | TranslationTuple, key: string) =>
      [key, translationTuple(message)]));
    const { messages: { notificationUsed } } = pipeline;
    const { confirmUsed, confirmNotUsed } = messages;
    const validationAction = pipeline.validate
      ? pipeline.validate.bindAs('validate.success', 'notifications')
      : goToStep('validate.success');
    const usedMessageAction = notificationUsed ?
      this.commonActions.notification(notificationUsed).bindAs('finish')
      : this.commonActions
        .yesNoConfirmation(confirmUsed.id, confirmUsed.params)
        .bindAs('execute.summary', 'finish');

    return <ActionFlow<S>>createFlow({
      'start': validationAction,
      'validate.success': pipeline.executes.checkUsage.bindAs('execute.checkUsage.success', 'notifications'),
      'execute.checkUsage.success': (dispatch: ActionDispatcher, state: S) => {
        const { deleted } = state.value;
        return dispatch(deleted ? 'notUsed' : 'used', state);
      },
      'notUsed': this.commonActions
        .yesNoConfirmation(confirmNotUsed.id, confirmNotUsed.params)
        .bindAs('execute.summary', 'finish'),
      'used': usedMessageAction,
      'execute.summary': pipeline.executes.summary.bindAs('execute.summary.success', 'notifications'),
      'execute.summary.success': (dispatch: ActionDispatcher, state: S) => {
        if (pipeline.successMessage) {
          const { id, params } = translationTuple(pipeline.successMessage);
          this.notificationService.success(id, params);
        }

        if (pipeline.finish) {
          pipeline.finish(state);
        }

        dispatch('finish', state);
      },
      'notifications': this.commonActions.notifications().bindAs('finish'),
      '*': exitFlow(),
    });
  }
}
